import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { axiosMaster, axiosSalary } from "../api/axiosInstance";

const AddEditEmployeeProfile = ({ editingData, onClose, onSuccess }) => {

    let storedUser = {};
    try {
        storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    } catch { }

    const groupId = storedUser?.groupId;
    const userId  = storedUser?.userId;

    const [employees, setEmployees] = useState([]);
    const [shifts,    setShifts]    = useState([]);

    const [form, setForm] = useState({
        empId:                  "",
        employeeName:           "",
        restId:                 "",
        shiftId:                "",
        expectedWorkingMinutes: "",
        isOvertimeAllowed:      false,

        basic:             "",
        advanceDeduction:  "",
        loanEMI:           "",
        staffExp:          "",
        paidLeavePerMonth: "",
        compOff:           "",
        fineApplied:       "",

        isPFApplicable:     false,
        isESICApplicable:   false,
        houseRentAllowance: false,
        otherAllowance1:    false,
        otherAllowance2:    false,
    });

    const [saving, setSaving] = useState(false);

    /* ================= REFS ================= */

    const empRef      = useRef(null);
    const shiftRef    = useRef(null);
    const workRef     = useRef(null);
    const basicRef    = useRef(null);
    const advanceRef  = useRef(null);
    const loanRef     = useRef(null);
    const staffExpRef = useRef(null);
    const leaveRef    = useRef(null);
    const compRef     = useRef(null);
    const fineRef     = useRef(null);
    const saveRef     = useRef(null);

    /* ================= LIFECYCLE ================= */

    useEffect(() => {
        fetchEmployees();
        fetchShifts();
        setTimeout(() => empRef.current?.focus(), 300);
    }, []);

    useEffect(() => {
        if (!editingData) return;
        setForm({
            empId:                  editingData.empId,
            employeeName:           editingData.employeeName,
            restId:                 editingData.restId                     || "",
            shiftId:                editingData.shiftId,
            expectedWorkingMinutes: editingData.expectedWorkingMinutes,
            isOvertimeAllowed:      editingData.isOvertimeAllowed          || false,

            basic:             editingData.salary?.basic              || "",
            advanceDeduction:  editingData.salary?.advanceDeduction   || "",
            loanEMI:           editingData.salary?.loanEMI            || "",
            staffExp:          editingData.salary?.staffExp           || "",
            paidLeavePerMonth: editingData.salary?.paidLeavePerMonth  || "",
            compOff:           editingData.salary?.compOff            || "",
            fineApplied:       editingData.salary?.fineApplied        || "",

            isPFApplicable:     editingData.salary?.isPFApplicable     || false,
            isESICApplicable:   editingData.salary?.isESICApplicable   || false,
            houseRentAllowance: editingData.salary?.houseRentAllowance || false,
            otherAllowance1:    editingData.salary?.otherAllowance1    || false,
            otherAllowance2:    editingData.salary?.otherAllowance2    || false,
        });
    }, [editingData]);

    /* ================= API ================= */

    const fetchEmployees = async () => {
        try {
            const res = await axiosMaster.get(`/EmployeeMaster/GetByGroup?groupId=${groupId}`);
            setEmployees(res.data.map(e => ({
                value:  e.empId,
                label:  e.empName,
                restId: e.restId,
            })));
        } catch {
            alert("Failed to load employees");
        }
    };

    const fetchShifts = async () => {
        try {
            const res = await axiosSalary.get(`/AttendanceShift/GetByGroup?groupId=${groupId}`);
            setShifts(res.data.map(s => ({
                value: s.shiftId,
                label: `${s.shiftName} (${s.reportingTime} - ${s.closingTime})`,
            })));
        } catch {
            alert("Failed to load shifts");
        }
    };

    /* ================= HELPERS ================= */

    const handleChange = (field, value) =>
        setForm(prev => ({ ...prev, [field]: value }));

    const handleKey = (e, nextRef, prevRef) => {
        const isSelect =
            e.target?.className?.includes("react-select") ||
            e.target?.id?.includes("react-select");

        if (isSelect && e.key === "Enter") return;

        if (e.key === "Enter") {
            e.preventDefault();
            nextRef?.current?.focus();
        }
        if (e.key === "Escape") {
            e.preventDefault();
            if (prevRef?.current) prevRef.current.focus();
            else onClose();
        }
    };

    /* ================= SAVE ================= */

    const handleSave = async () => {
        if (!form.empId)   return alert("Select employee");
        if (!form.shiftId) return alert("Select shift");

        const payload = {
            groupId:                Number(groupId),
            restId:                 Number(form.restId),
            empId:                  Number(form.empId),
            employeeName:           form.employeeName,
            shiftId:                Number(form.shiftId),
            expectedWorkingMinutes: Number(form.expectedWorkingMinutes  || 0),
            isOvertimeAllowed:      form.isOvertimeAllowed,
            salary: {
                basic:              Number(form.basic             || 0),
                dearnessAllowance:  true,
                houseRentAllowance: form.houseRentAllowance,
                otherAllowance1:    form.otherAllowance1,
                otherAllowance2:    form.otherAllowance2,
                isPFApplicable:     form.isPFApplicable,
                isESICApplicable:   form.isESICApplicable,
                advanceDeduction:   Number(form.advanceDeduction  || 0),
                loanEMI:            Number(form.loanEMI           || 0),
                staffExp:           Number(form.staffExp          || 0),
                paidLeavePerMonth:  Number(form.paidLeavePerMonth || 0),
                compOff:            Number(form.compOff           || 0),
                fineApplied:        Number(form.fineApplied       || 0),
            },
            userId: Number(userId),
        };

        try {
            setSaving(true);
            await axiosSalary.post("/EmployeeProfile/UpsertProfile", payload);
            onSuccess?.();
            onClose?.();
        } catch {
            alert("Save failed");
        } finally {
            setSaving(false);
        }
    };

    /* ================= RENDER ================= */

    const inp = "w-full border px-2 py-1.5 rounded text-xs";

    return (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
            <div className="bg-white w-full max-w-sm rounded-lg shadow-lg">

                {/* HEADER */}
                <div className="flex justify-between items-center px-3 py-2 border-b">
                    <h2 className="font-semibold text-sm">
                        {editingData ? "Edit" : "Add"} Employee Profile
                    </h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-black text-base leading-none">✕</button>
                </div>

                {/* BODY */}
                <div className="px-3 py-2 space-y-2 text-xs max-h-[75vh] overflow-y-auto">

                    {/* Employee */}
                    <div>
                        <label className="text-[11px] font-medium">Employee</label>
                        <Select
                            ref={empRef}
                            options={employees}
                            value={employees.find(e => e.value === form.empId) || null}
                            onChange={(opt) => {
                                handleChange("empId",        opt.value);
                                handleChange("employeeName", opt.label);
                                handleChange("restId",       opt.restId);
                                shiftRef.current?.focus();
                            }}
                            onKeyDown={(e) => handleKey(e, shiftRef, null)}
                            openMenuOnFocus
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        />
                    </div>

                    {/* Shift */}
                    <div>
                        <label className="text-[11px] font-medium">Shift</label>
                        <Select
                            ref={shiftRef}
                            options={shifts}
                            value={shifts.find(s => s.value === form.shiftId) || null}
                            onChange={(opt) => {
                                handleChange("shiftId", opt.value);
                                workRef.current?.focus();
                            }}
                            onKeyDown={(e) => handleKey(e, workRef, empRef)}
                            openMenuOnFocus
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        />
                    </div>

                    {/* Numeric inputs
                        Enter chain: work → basic → advance → loan → staffExp → leave → comp → fine → save
                        Escape chain: reverses the above
                    */}
                    <div className="grid grid-cols-2 gap-2">

                        <div>
                            <label className="text-[11px]">Working Min</label>
                            <input
                                ref={workRef}
                                value={form.expectedWorkingMinutes}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => handleChange("expectedWorkingMinutes", e.target.value)}
                                onKeyDown={(e) => handleKey(e, basicRef, shiftRef)}
                                className={inp}
                            />
                        </div>

                        <div>
                            <label className="text-[11px]">Basic Salary</label>
                            <input
                                ref={basicRef}
                                value={form.basic}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => handleChange("basic", e.target.value)}
                                onKeyDown={(e) => handleKey(e, advanceRef, workRef)}
                                className={inp}
                            />
                        </div>

                        <div>
                            <label className="text-[11px]">Advance Deduction</label>
                            <input
                                ref={advanceRef}
                                value={form.advanceDeduction}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => handleChange("advanceDeduction", e.target.value)}
                                onKeyDown={(e) => handleKey(e, loanRef, basicRef)}
                                className={inp}
                            />
                        </div>

                        <div>
                            <label className="text-[11px]">Loan EMI</label>
                            <input
                                ref={loanRef}
                                value={form.loanEMI}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => handleChange("loanEMI", e.target.value)}
                                onKeyDown={(e) => handleKey(e, staffExpRef, advanceRef)}
                                className={inp}
                            />
                        </div>

                        <div>
                            <label className="text-[11px]">Staff Expense</label>
                            <input
                                ref={staffExpRef}
                                value={form.staffExp}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => handleChange("staffExp", e.target.value)}
                                onKeyDown={(e) => handleKey(e, leaveRef, loanRef)}
                                className={inp}
                            />
                        </div>

                        <div>
                            <label className="text-[11px]">Paid Leave / Month</label>
                            <input
                                ref={leaveRef}
                                value={form.paidLeavePerMonth}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => handleChange("paidLeavePerMonth", e.target.value)}
                                onKeyDown={(e) => handleKey(e, compRef, staffExpRef)}
                                className={inp}
                            />
                        </div>

                        <div>
                            <label className="text-[11px]">Comp Off</label>
                            <input
                                ref={compRef}
                                value={form.compOff}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => handleChange("compOff", e.target.value)}
                                onKeyDown={(e) => handleKey(e, fineRef, leaveRef)}
                                className={inp}
                            />
                        </div>

                        <div>
                            <label className="text-[11px]">Fine</label>
                            <input
                                ref={fineRef}
                                value={form.fineApplied}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => handleChange("fineApplied", e.target.value)}
                                onKeyDown={(e) => handleKey(e, saveRef, compRef)}
                                className={inp}
                            />
                        </div>

                    </div>

                    {/* Checkboxes */}
                    <div className="grid grid-cols-2 gap-2 mt-2 text-[11px]">

                        <label className="flex items-center gap-1 cursor-pointer">
                            <input type="checkbox"
                                checked={form.isOvertimeAllowed}
                                onChange={(e) => handleChange("isOvertimeAllowed", e.target.checked)}
                            />
                            Allow Overtime
                        </label>

                        <label className="flex items-center gap-1 cursor-pointer">
                            <input type="checkbox"
                                checked={form.isPFApplicable}
                                onChange={(e) => handleChange("isPFApplicable", e.target.checked)}
                            />
                            PF Applicable
                        </label>

                        <label className="flex items-center gap-1 cursor-pointer">
                            <input type="checkbox"
                                checked={form.isESICApplicable}
                                onChange={(e) => handleChange("isESICApplicable", e.target.checked)}
                            />
                            ESIC Applicable
                        </label>

                        <label className="flex items-center gap-1 cursor-pointer">
                            <input type="checkbox"
                                checked={form.houseRentAllowance}
                                onChange={(e) => handleChange("houseRentAllowance", e.target.checked)}
                            />
                            HRA
                        </label>

                        <label className="flex items-center gap-1 cursor-pointer">
                            <input type="checkbox"
                                checked={form.otherAllowance1}
                                onChange={(e) => handleChange("otherAllowance1", e.target.checked)}
                            />
                            Other Allowance 1
                        </label>

                        <label className="flex items-center gap-1 cursor-pointer">
                            <input type="checkbox"
                                checked={form.otherAllowance2}
                                onChange={(e) => handleChange("otherAllowance2", e.target.checked)}
                            />
                            Other Allowance 2
                        </label>

                    </div>
                </div>

                {/* FOOTER */}
                <div className="flex justify-end px-3 py-2 border-t gap-2">
                    <button
                        onClick={onClose}
                        className="border px-3 py-1 rounded text-xs hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        ref={saveRef}
                        onClick={handleSave}
                        disabled={saving}
                        onKeyDown={(e) => {
                            if (e.key === "Enter")  handleSave();
                            if (e.key === "Escape") fineRef.current?.focus();
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs disabled:opacity-60"
                    >
                        {saving ? "Saving..." : "Save"}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default AddEditEmployeeProfile;